import React from 'react';
import { useRouter } from 'next/router';
import { FixedLines, MainHeading } from '@/components/Views/Heading';
import { NormalText } from '@/components/Views/Text';
import RightArrow from '@/components/SVG/right-arrow.svg';
import LargeButton from '@/components/Views/Buttons';
import IconBlock, { floatingIcon } from '@/components/Animations/IconBlock';
import GradientBackground from '@/components/Gradient/GradientBackground';
import BalanceText from '@/components/BalanceText/BalanceText';
import Image from '@/components/ReusableUI/Image';
import StructuredText from '@/components/ReusableUI/StructuredText';
import { MUZOLOGY_SIGNUP_URL } from '@/lib/defaults';

interface Props {
    id?: string;
    heading?: string;
    featuredImage?: any;
    content?: any;
    subhead?: any;
    clickThrough?: any;
    buttonText?: string;
    desktopFloatingIcons?: any;
    mobileFloatingIcons?: any;
    bannerText?: string;
    ctaURL?: string;
    disableFloatingIcons?: boolean;
}

const TopBanner = ({ className, children }) => {
    return (
        <div className={'z-10 absolute top-0 left-0 right-0 p-4 bg-MuzologyDarkPurple justify-center items-center inline-flex ' + className}>
            <div className='default-max-w mx-auto w-full'>
                <div className='text-center text-neutral-200 text-xl lg:text-2xl font-semibold lg:leading-10 lg:tracking-wider'>
                    <BalanceText>{children}</BalanceText>
                </div>
            </div>
        </div>
    );
};

const ParentsHero = ({ id, heading, featuredImage, subhead, clickThrough, content = null, buttonText, desktopFloatingIcons, mobileFloatingIcons, bannerText, ctaURL, disableFloatingIcons }: Props) => {
    const router = useRouter();
    // 64px 40px 0px
    // heading = 'The award-winning\nmath solution that gets\nyour child excited\nabout learning.';
    // heading = 'The award-winning math solution that gets your child excited about learning.';

    const defaultDesktopFloatingIconsPositions = [
        floatingIcon('75%', '18%', '/icons/float-icon-blue-func.svg', 'w-20 md:w-24 lg:w-26 xl:w-28', 0, 1.2),
        floatingIcon('1%', '6%', '/icons/float-icon-orange-note.svg', 'w-14 md:w-18 lg:w-18 xl:w-20', 0, 1.4),
        floatingIcon('18%', '80%', '/icons/float-icon-white-pi.svg', 'w-14 md:w-12 lg:w-26 xl:w-28', 0, 1.5)
    ];
    const defaultMobileFloatingIconsPositions = [
        floatingIcon('74%', '18%', '/icons/float-icon-blue-func.svg', 'w-20 md:w-24 lg:w-26 xl:w-28', 0, 1.4),
        floatingIcon('2%', '6%', '/icons/float-icon-orange-note.svg', 'w-14 md:w-18 lg:w-18 xl:w-20', 0, 1.6),
        floatingIcon('18%', '80%', '/icons/float-icon-white-pi.svg', 'w-14 md:w-12 lg:w-26 xl:w-28', 0, 1.8)
    ];

    const mobileFloatingIconsPositions = mobileFloatingIcons ?? defaultMobileFloatingIconsPositions;
    const desktopFloatingIconsPositions = desktopFloatingIcons ?? defaultDesktopFloatingIconsPositions;

    return (
        <section
            id={id}
            className={`
                relative
                hero-container
                pb-[10px]
                px-5
                lg:px-[60px] 
                pt-[64px] 
                lg:pt-[160px]
            `}
            style={{ background: 'linear-gradient(180deg, #9184FF 1.37%, #F1DFF1 80%)' }}
        >

            <GradientBackground />
            {bannerText && <TopBanner className='top-[80px] px-[30px] py-[13px] bg-black tracking-wider'>{bannerText}</TopBanner>}
            <div
                id='parent-hero-content'
                className={`${bannerText ? 'mt-[160px] xs:mt-[120px] sm:mt-[80px] lg:mt-0' : ''} relative z-100 flex flex-col lg:flex-row mx-auto my-0 items-center justify-center hero-max-w 0border-solid`}
            >
                <div id='parent-hero-left' className='w-full px-0 md:px-16 lg:pl-0 lg:pr-10 0bg-red-100 0border-solid'>
                    <div className='relative flex justify-center md:max-w-[680px] bg-red-30 mx-auto lg:ml-auto'>
                        <Image image={featuredImage} priority={true} />
                        {!disableFloatingIcons && <>
                            <IconBlock icons={desktopFloatingIconsPositions} className='hidden lg:block' />
                            <IconBlock icons={mobileFloatingIconsPositions} className='hidden xs:block lg:hidden' />
                        </>}
                    </div>
                </div>

                <div id='parent-hero-right' className='w-full lg:mt-0 0border-solid flex flex-col gap-4'>
                    <MainHeading className='opacity-100 mix-blend-color-burn'>
                        <BalanceText>{heading}</BalanceText>
                        {/*<FixedLines text={heading} />*/}
                    </MainHeading>
                    {/*<NormalText className='text-center mb-0.5'>{`It's fun, it's easy, and your child will love it! Muzology uses engaging music videos to teach math like never before.`}</NormalText>*/}
                    <NormalText className='text-left md:text-center max-w-[615px] w-full mx-auto leading-6 p-0 my-0'>
                        {subhead}
                    </NormalText>

                    {/*<StructuredText data={content} className='hero-text' />*/}
                    {/*
                    <NormalText className='text-center mb-0.5'>{`It's fun, it's easy, and your child will love it!`}</NormalText>
                    <NormalText className='text-center my-0.5'> Muzology uses engaging music videos to teach math like never before. </NormalText>
                    */}
                    {!clickThrough && <NormalText className='text-center m-0 font-bold'>Recommended for students in grades 4-8</NormalText>}

                    <div className='relative flex justify-center '>
                        <LargeButton
                            id={id + '-start-free-trial'}
                            data-heap='parent-hero-start-free-trial'
                            // className='white-button purple-text px-8 mb-6 sm:mb-10 lg:mb-0'
                            className='purple-button purple-text px-8 mb-6 sm:mb-10 lg:mb-0'
                            text={buttonText ?? 'Start Free Trial Now'}
                            href={MUZOLOGY_SIGNUP_URL}
                            rightArrow={true}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ParentsHero;
